import { useMemo } from 'react';
import { Employee, FilterState } from '../types';
import { filterBoardEmployees } from '../utils/filterEmployees';
import { countEmployeeAbsencesInMonth } from '../utils/absences';
import { getProbationPeriodFromHiring } from '../probationPeriod';

/**
 * Filtra funcionários por busca e filtros ativos (memoizado pelas dependências).
 */
export function useFilteredEmployees(
  items: Record<string, Employee[]>,
  columns: { id: string; title: string; color?: string }[],
  search: string,
  filters: FilterState,
  todayStr: string,
  timeOffset: number
): Record<string, Employee[]> {
  return useMemo(() => {
    const referenceDate = new Date(Date.now() + timeOffset);
    return filterBoardEmployees({ items, columns, search, filters, todayStr, referenceDate });
  }, [items, columns, search, filters, todayStr, timeOffset]);
}

/**
 * Hook otimizado para calcular estatísticas
 */
export function useEmployeeStats(
  items: Record<string, Employee[]>,
  columns: { id: string; title: string; color?: string }[],
  hiringLimits: Record<string, number>,
  timeOffset: number
) {
  return useMemo(() => {
    const allEmployees = Object.values(items).flat() as Employee[];
    const referenceDate = new Date(Date.now() + timeOffset);

    // Contadores otimizados com reduce único
    const counts = allEmployees.reduce(
      (acc, emp) => {
        acc.total++;
        const probation = getProbationPeriodFromHiring(emp.hiringDate, referenceDate);
        if (probation === '45') acc.probation45++;
        if (probation === '90') acc.probation90++;
        acc.absencesMonth += countEmployeeAbsencesInMonth(emp, referenceDate);
        return acc;
      },
      { total: 0, probation45: 0, probation90: 0, absencesMonth: 0 }
    );

    // Cálculo de vagas
    const hiring = columns.reduce((sum, col) => {
      const limit = hiringLimits[col.id] || 0;
      const current = items[col.id]?.length || 0;
      return sum + Math.max(0, limit - current);
    }, 0);

    return {
      ...counts,
      away: (items['afastados'] || items['afastados_adm'])?.length || 0,
      hiring,
    };
  }, [items, columns, hiringLimits, timeOffset]);
}
